// frontend/src/components/KiteLogin.js
// ----------------------------------------------------
// Zerodha Kite Login Component - Modern UI
// ----------------------------------------------------

import React, { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE } from "../config";

export default function KiteLogin() {
  const [status, setStatus] = useState("Checking session...");
  const [accessToken, setAccessToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [schedulerStarted, setSchedulerStarted] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        // 1️⃣ Check existing session via /auth/session
        const { data } = await axios.get(`${API_BASE}/auth/session`);
        if (data && data.ok && data.access_token) {
          setAccessToken(data.access_token);
          setStatus("✅ Connected to Zerodha");
          setLoading(false);
          return;
        }
      } catch (err) {
        console.warn("Session check via /auth/session failed:", err.message);
      }

      // 1b️⃣ Fallback: Check if we can fetch positions (proves we're logged in)
      try {
        const { data: posData } = await axios.get(`${API_BASE}/kite/positions`);
        if (posData && posData.ok) {
          // We're logged in even if session check failed
          setAccessToken("(connected)"); // Indicate we're connected
          setStatus("✅ Connected to Zerodha");
          setLoading(false);
          return;
        }
      } catch (err) {
        console.warn("Fallback positions check failed:", err.message);
      }

      // 2️⃣ Handle callback from Zerodha login redirect
      const params = new URLSearchParams(window.location.search);
      const reqToken = params.get("request_token");

      // Avoid processing same callback multiple times
      if (window.sessionStorage.getItem("kite_callback_done") === "true") {
        console.log("⚠️ Callback already processed, skipping duplicate.");
        setLoading(false);
        return;
      }

      if (reqToken) {
        window.sessionStorage.setItem("kite_callback_done", "true");
        try {
          setStatus("🔄 Exchanging request_token for access token...");
          const { data } = await axios.get(
            `${API_BASE}/auth/kite-callback?request_token=${reqToken}`
          );

          if (data && data.access_token) {
            setAccessToken(data.access_token);
            setStatus("✅ Connected to Zerodha");

            // Clean URL (remove ?request_token)
            window.history.replaceState({}, document.title, window.location.pathname);

            // Auto-start scheduler (Live)
            await startSchedulerOnce();
          } else {
            setStatus("❌ Login failed: No access_token returned.");
          }
        } catch (err) {
          setStatus("❌ Login failed: " + (err.response?.data?.error || err.message));
        }
      } else {
        setStatus("Please login to start live trading.");
      }

      setLoading(false);
    })();
  }, []);

  // ✅ Helper: Start scheduler safely (only once)
  const startSchedulerOnce = async (mode = "live") => {
    if (schedulerStarted) {
      console.log("⚠️ Scheduler already started — skipping duplicate call.");
      return;
    }

    try {
      setSchedulerStarted(true);
      setStatus(`📈 Starting ${mode} scheduler...`);

      const { data } = await axios.post(
        `${API_BASE}/strategy/start`,
        { mode }
      );

      if (data?.ok === false) throw new Error(data.error || "Unknown backend error");

      setStatus(`✅ ${mode.toUpperCase()} trading started successfully.`);
    } catch (err) {
      if (err.message.includes("already")) {
        setStatus("⚠️ Scheduler already running.");
      } else {
        setStatus("❌ Failed to start scheduler: " + err.message);
        setSchedulerStarted(false);
      }
    }
  };

  // 3️⃣ Start login flow
  const startLogin = async () => {
    try {
      setStatus("🔗 Redirecting to Zerodha Kite login...");
      const { data } = await axios.get(`${API_BASE}/auth/login`);
      if (data && data.login_url) {
        window.location.href = data.login_url;
      } else {
        setStatus("❌ Failed to get login URL from backend.");
      }
    } catch (err) {
      setStatus("❌ Failed to initiate login: " + err.message);
    }
  };

  // 4️⃣ Logout
  const logout = async () => {
    try {
      await axios.get(`${API_BASE}/auth/logout`);
      setAccessToken(null);
      setSchedulerStarted(false);
      setStatus("🚪 Logged out. Please login again.");
      setTimeout(() => {
        window.location.href = "/kite";
        window.location.reload();
      }, 1000);
    } catch (err) {
      setStatus("❌ Failed to logout: " + err.message);
    }
  };

  // ─────────────────────────────────────────────
  // UI with modern styling
  // ─────────────────────────────────────────────
  return (
    <div className="kite-login-container">
      {loading && (
        <div className="kite-loading">
          <div className="loading-spinner"></div>
          <p>Checking session...</p>
        </div>
      )}

      {!loading && !accessToken && (
        <div className="kite-not-connected">
          <div className="kite-icon-large">🔌</div>
          <h3>Connect to Zerodha</h3>
          <p className="kite-description">
            Link your Zerodha Kite account to enable live trading with real-time market data.
          </p>
          <button onClick={startLogin} className="btn btn-primary btn-large">
            <span>🔑</span>
            Login with Zerodha Kite
          </button>
        </div>
      )}

      {accessToken && (
        <div className="kite-connected">
          <div className="kite-status-header">
            <div className="kite-connected-icon">
              <span className="status-dot online"></span>
              <span>✅</span>
            </div>
            <div>
              <h3>Connected to Zerodha</h3>
              <p className="text-muted">Your account is linked and ready for trading</p>
            </div>
          </div>

          <div className="kite-token-display">
            <label>Access Token</label>
            <code className="token-code">{accessToken}</code>
          </div>

          <div className="kite-actions">
            <button onClick={logout} className="btn btn-danger">
              <span>🚪</span>
              Disconnect & Logout
            </button>
          </div>
        </div>
      )}

      <div className="kite-status-message">
        <p>{status}</p>
      </div>

      <style>{`
        .kite-login-container {
          max-width: 500px;
          margin: 0 auto;
        }

        .kite-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
          padding: 40px;
          color: var(--color-text-secondary);
        }

        .kite-not-connected {
          text-align: center;
          padding: 40px 20px;
        }

        .kite-icon-large {
          font-size: 4rem;
          margin-bottom: 20px;
          opacity: 0.8;
        }

        .kite-not-connected h3 {
          font-size: 1.5rem;
          margin-bottom: 12px;
          color: var(--color-text-primary);
        }

        .kite-description {
          color: var(--color-text-secondary);
          margin-bottom: 24px;
          line-height: 1.6;
        }

        .btn-large {
          padding: 14px 28px;
          font-size: 1rem;
        }

        .kite-connected {
          padding: 20px 0;
        }

        .kite-status-header {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 20px;
          background: var(--color-success-bg);
          border: 1px solid rgba(34, 197, 94, 0.3);
          border-radius: var(--radius-md);
          margin-bottom: 24px;
        }

        .kite-connected-icon {
          font-size: 2rem;
          position: relative;
        }

        .kite-connected-icon .status-dot {
          position: absolute;
          bottom: 0;
          right: 0;
        }

        .kite-status-header h3 {
          margin: 0 0 4px 0;
          color: var(--color-success-light);
        }

        .kite-token-display {
          margin-bottom: 24px;
        }

        .kite-token-display label {
          display: block;
          font-size: 0.875rem;
          color: var(--color-text-secondary);
          margin-bottom: 8px;
        }

        .token-code {
          display: block;
          width: 100%;
          padding: 12px 16px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          font-family: var(--font-family-mono);
          font-size: 0.85rem;
          word-break: break-all;
          color: var(--color-accent);
        }

        .kite-actions {
          display: flex;
          justify-content: center;
        }

        .kite-status-message {
          margin-top: 24px;
          padding: 16px;
          background: rgba(255, 255, 255, 0.03);
          border-radius: var(--radius-md);
          text-align: center;
        }

        .kite-status-message p {
          margin: 0;
          color: var(--color-text-secondary);
          font-size: 0.9rem;
        }
      `}</style>
    </div>
  );
}